import { FC, useMemo } from 'react';

import {
	EvSection,
	EvSwitch,
	OptionType,
	TABLE_FILTERS_SR_SUMMARY,
	TEvFiltersPanelValues
} from '@evinced-private/ui-common';

import { MFA_SESSION_FILTERS_SECTION_ID } from 'data-automation-ids/MfaDashboardAutomationIds';
import { useMfaSessionFilterOptionsQuery } from 'src/apps/mfa/hooks/useMfaSessionFilterOptionsQuery';
import { useMfaSingleSessionFilters } from 'src/apps/mfa/hooks/useMfaSingleSessionFilters';
import { TMfaSessionIssuesFilterOptions } from 'src/apps/mfa/services/MfaDashboardServiceTypes';
import { resolveScanOptions } from 'src/apps/mobile-common/helpers/resolveScanOptions';
import { useIncludeZeroIssueToggle } from 'src/apps/mobile-common/hooks/useIncludeZeroIssueToggle';
import { MOBILE_SCANS_FILTERS_LABELS } from 'src/apps/mobile-common/labels/MobileScansFilters.labels';
import { ProductFiltersPanel } from 'src/common/components/pinned-date-range-filters-panel';
import { PLATFORM_APP_ID } from 'src/common/consts/dom-consts';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import {
	buildPanelInitialValues,
	mapToPanelDefinitions
} from 'src/common/helpers/FiltersPanelHelper';

import './MfaSingleSessionFiltersSection.scss';

interface IMfaSingleSessionFiltersProps {
	sessionId: string;
	filters: IFilterOptions;
	onFilterChange: (tableId: string, filters: IFilterOptions) => void;
	tableId: string;
	ariaLabel: string;
	showIncludeZeroIssueToggle?: boolean;
	includeZeroIssueToggleDataAutomationId?: string;
}

export const MfaSingleSessionFiltersSection: FC<IMfaSingleSessionFiltersProps> = ({
	sessionId,
	filters,
	onFilterChange,
	tableId,
	ariaLabel,
	showIncludeZeroIssueToggle = false,
	includeZeroIssueToggleDataAutomationId
}) => {
	const filterOptionsQuery = useMfaSessionFilterOptionsQuery(sessionId);

	const { includeScansWithoutIssues, handleApply, handleReset, handleToggleChange } =
		useIncludeZeroIssueToggle({ tableId, filters, onFilterChange, showIncludeZeroIssueToggle });

	const scanOptions = resolveScanOptions(filterOptionsQuery.data, includeScansWithoutIssues);

	const { filterDefinitions, mergedFilterOptions } = useMfaSingleSessionFilters({
		allLabels: scanOptions,
		selectedLabels: ((filters as TMfaSessionIssuesFilterOptions)?.scans as OptionType[]) ?? [],
		filterOptions: filterOptionsQuery.data ?? {}
	});

	const panelDefinitions = useMemo(
		() => mapToPanelDefinitions(filterDefinitions),
		[filterDefinitions]
	);

	const initialValues = useMemo<TEvFiltersPanelValues>(
		() => buildPanelInitialValues(panelDefinitions, filters),
		[panelDefinitions, filters]
	);

	return (
		<EvSection
			ariaLabel={ariaLabel}
			ariaDescribedby={TABLE_FILTERS_SR_SUMMARY}
			ariaLive="polite"
			skipLinkId="filters-section"
			dataAutomationId={MFA_SESSION_FILTERS_SECTION_ID}
			className="mfa-single-session-filters-section"
		>
			<ProductFiltersPanel
				appElement={PLATFORM_APP_ID}
				onApply={handleApply}
				onReset={handleReset}
				initialValues={initialValues}
				filterOptions={mergedFilterOptions}
				filterDefinitions={panelDefinitions}
			/>
			{showIncludeZeroIssueToggle && (
				<EvSwitch
					dataAutomationId={includeZeroIssueToggleDataAutomationId}
					onChange={handleToggleChange}
					checked={includeScansWithoutIssues}
					label={MOBILE_SCANS_FILTERS_LABELS.INCLUDE_ZERO_ISSUE_TOGGLE_LABEL}
				/>
			)}
		</EvSection>
	);
};
